// src/pages/RedemittelPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, ChevronRight, Check, X, ChevronLeft as BackIcon, BarChart3, Sparkles, Home, LayoutGrid, BookOpen } from 'lucide-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { AnkiSRS, Rating } from '../services/srsService';
import { logRedemittelLearning } from '../utils/activityLogger';
import { trackPaywallShown } from '../utils/conversionEvents';
import subscriptionService from '../services/subscriptionService';
import AhaUpsellModal from '../components/ui/AhaUpsellModal';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import Confetti from 'react-confetti';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';

// CSS Animations (inline styles to match VocabularyPage)
const cardStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }

  /* Card Hover Effects */
  .redemittel-category-card {
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .redemittel-category-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    background-color: #000000; /* black */
  }

  .redemittel-category-card:hover .redemittel-content-animated {
    transform: translateY(-2px);
  }

  /* Shine Effect */
  .redemittel-shine-effect {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    transform: rotate(45deg);
    transition: all 0.6s;
    opacity: 0;
  }

  .redemittel-category-card:hover .redemittel-shine-effect {
    animation: shine 0.6s;
  }

  @keyframes shine {
    from {
      left: -50%;
      opacity: 0;
    }
    to {
      left: 150%;
      opacity: 1;
    }
  }

  /* Decorative Dots */
  .redemittel-decorative-dot {
    position: absolute;
    width: 8px;
    height: 8px;
    background: currentColor;
    border-radius: 50%;
    opacity: 0.2;
    transition: all 0.3s;
  }

  .redemittel-dot-1 {
    top: 1rem;
    right: 3rem;
    animation: float 3s ease-in-out infinite;
  }

  .redemittel-dot-2 {
    bottom: 2rem;
    left: 2rem;
    animation: float 3s ease-in-out infinite 0.5s;
  }

  .redemittel-dot-3 {
    top: 50%;
    right: 1.5rem;
    animation: float 3s ease-in-out infinite 1s;
  }

  .redemittel-category-card:hover .redemittel-decorative-dot {
    opacity: 0.4;
    transform: scale(1.2);
  }

  /* Icon Animation */
  .redemittel-icon-animated {
    transition: all 0.3s ease;
  }

  .redemittel-category-card:hover .redemittel-icon-animated {
    transform: scale(1.1) rotate(-5deg);
  }

  /* Content Animation */
  .redemittel-content-animated {
    transition: all 0.3s ease;
  }
  
  .redemittel-content-animated h4,
  .redemittel-content-animated p {
    transition: color 0.3s ease;
  }
  
  .redemittel-category-card:hover .redemittel-content-animated h4 {
    color: white;
  }
  
  .redemittel-category-card:hover .redemittel-content-animated p {
    color: rgba(255, 255, 255, 0.8);
  }
  
  /* Icon color transition */
  .redemittel-icon-animated {
    transition: all 0.3s ease;
  }
  
  .redemittel-category-card:hover .redemittel-icon-animated {
    background-color: white;
  }
  
  /* Corner decorations turn white on hover */
  .redemittel-category-card .corner-decoration {
    transition: border-color 0.3s ease;
  }
  
  .redemittel-category-card:hover .corner-decoration {
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
`;

// Import new and existing components
import RedemittelCard from '../components/redemittel/RedemittelCard';
import SwipeIndicators from '../components/vocabulary/SwipeIndicators';
import ReviewButtons from '../components/vocabulary/ReviewButtons';
import RedemittelStats from '../components/redemittel/RedemittelStats';

// --- Helper Functions for Redemittel Structure ---
const getTelcSprechenParts = (level) => {
    const parts = { b1: [{ id: "teil1_vorstellen", name: "Teil 1: Sich vorstellen/Kontakt aufnehmen", icon: "👋" }, { id: "teil2_erfahrungen", name: "Teil 2: Über Erfahrungen sprechen", icon: "💭" }, { id: "teil3_planen", name: "Teil 3: Gemeinsam etwas planen", icon: "📅" }], b2: [{ id: "teil1_praesentation", name: "Teil 1: Präsentation", icon: "🎤" }, { id: "teil2_diskussion", name: "Teil 2: Diskussion", icon: "💭" }, { id: "teil3_problemloesung", name: "Teil 3: Gemeinsam eine Aufgabe lösen/Problemlösung", icon: "🔧" }], c1: [{ id: "teil1_praesentation", name: "Teil 1: Präsentation", icon: "🎤" }, { id: "teil2_diskussion", name: "Teil 2: Diskussion im Anschluss an die Präsentation", icon: "💭" }] };
    return parts[level] || [];
};

const getTelcSchreibenParts = (level) => {
    const parts = {
        b1: [{ id: "aufgabe_email_brief", name: "Formelle E-Mail/Brief", icon: "📧" }, { id: "informelle_email", name: "Informelle E-Mail", icon: "📨" }],
        b2: [{ id: "aufgabe_formell", name: "Eine Aufgabe: Formeller Brief oder E-Mail", icon: "📧" }],
        c1: [{ id: "aufgabe_essay", name: "Wahlaufgabe: Stellungnahme/Essay", icon: "📝" }, { id: "aufgabe_email_und_wahlaufgabe", name: "Pflichtaufgabe: E-Mail + prüfungsnahe Formate", icon: "📧" }]
    };
    return parts[level] || [];
};

const getGoetheSprechenParts = (level) => {
    const parts = { b1: [{ id: "teil1_planen", name: "Teil 1: Gemeinsam etwas planen", icon: "📅" }, { id: "teil2_praesentation", name: "Teil 2: Präsentation eines Themas", icon: "🎤" }, { id: "teil3_gespraech", name: "Teil 3: Gespräch über die Präsentationen", icon: "💬" }], b2: [{ id: "teil1_vortrag", name: "Teil 1: Vortrag/Präsentation", icon: "🎤" }, { id: "teil2_diskussion", name: "Teil 2: Diskussion", icon: "💭" }], c1: [{ id: "teil1_vortrag", name: "Teil 1: Vortrag/Präsentation", icon: "🎤" }, { id: "teil2_diskussion", name: "Teil 2: Diskussion", icon: "💭" }] };
    return parts[level] || [];
};

const getGoetheSchreibenParts = (level) => {
    const parts = {
        b1: [{ id: "aufgabe1_informell", name: "Aufgabe 1: Informelle E-Mail", icon: "✉️" }, { id: "aufgabe2_forum", name: "Aufgabe 2: Forumsbeitrag", icon: "💬" }, { id: "aufgabe3_formell", name: "Aufgabe 3: Formelle/halbformelle Nachricht", icon: "📧" }],
        b2: [{ id: "teil1_forum", name: "Teil 1: Forumsbeitrag", icon: "💬" }, { id: "teil2_formell", name: "Teil 2: Formelle Nachricht/E-Mail", icon: "📧" }],
        c1: [{ id: "teil3_pruefungsnahe_aufgaben", name: "Teil 1+2: Forumbeitrag und formelle Beschwerde", icon: "🧭" }, { id: "teil1_aufsatz", name: "Akademische Argumentation/Grafikanalyse", icon: "📊" }]
    };
    return parts[level] || [];
};

const getOsdSprechenParts = (level) => {
    const parts = { b1: [{ id: "teil1_planen", name: "Teil 1: Gemeinsam etwas planen", icon: "📅" }, { id: "teil2_kurzvortrag", name: "Teil 2: Kurzvortrag/Präsentation", icon: "🎤" }, { id: "teil3_diskussion", name: "Teil 3: Diskussion/Fragen zur Präsentation", icon: "❓" }], b2: [{ id: "teil1_praesentation", name: "Teil 1: Ein Thema präsentieren und darüber sprechen", icon: "🎤" }, { id: "teil2_bild", name: "Teil 2: Bildbeschreibung und Diskussion", icon: "🖼️" }, { id: "teil3_diskussion", name: "Teil 3: Diskussion", icon: "💭" }], c1: [{ id: "teil1_telefon", name: "Teil 1: Telefongespräch", icon: "📞" }, { id: "teil2_diskussion", name: "Teil 2: Diskussion", icon: "💭" }, { id: "teil3_kurzreferat", name: "Teil 3: Kurzreferat/Präsentation", icon: "🎤" }] };
    return parts[level] || [];
};

const getOsdSchreibenParts = (level) => {
    const parts = { b1: [{ id: "formelle_email", name: "Formelle E-Mail", icon: "📧" }, { id: "informelle_email", name: "Informelle E-Mail", icon: "✉️" }], b2: [{ id: "aufgabe1_brief", name: "Aufgabe 1: Formeller Brief", icon: "📧" }, { id: "aufgabe2_argument", name: "Aufgabe 2: Argumentativer Beitrag/Meinungsäußerung", icon: "💭" }], c1: [{ id: "aufgabe1_formell", name: "Aufgabe 1: Formelle E-Mail/Brief", icon: "📧" }, { id: "aufgabe2_referat", name: "Aufgabe 2: Schriftliches Referat, Stellungnahme oder Kommentar", icon: "📝" }] };
    return parts[level] || [];
};

const getExactProviderStructure = (provider, level) => {
    const providers = {
        telc: {
            name: "TELC",
            icon: "🇪🇺",
            description: `TELC-spezifische Redemittel für ${level.toUpperCase()}`,
            skills: {
                sprechen: { name: "Sprechen", icon: "🗣️", parts: getTelcSprechenParts(level) },
                schreiben: { name: "Schreiben", icon: "✍️", parts: getTelcSchreibenParts(level) }
            }
        },
        goethe: {
            name: "Goethe",
            icon: "🇩🇪",
            description: `Goethe-spezifische Redemittel für ${level.toUpperCase()}`,
            skills: {
                sprechen: { name: "Sprechen", icon: "🗣️", parts: getGoetheSprechenParts(level) },
                schreiben: { name: "Schreiben", icon: "✍️", parts: getGoetheSchreibenParts(level) }
            }
        },
        osd: {
            name: "ÖSD",
            icon: "🇦🇹",
            description: `ÖSD-spezifische Redemittel für ${level.toUpperCase()}`,
            skills: {
                sprechen: { name: "Sprechen", icon: "🗣️", parts: getOsdSprechenParts(level) },
                schreiben: { name: "Schreiben", icon: "✍️", parts: getOsdSchreibenParts(level) }
            }
        }
    };
    return providers[provider];
};

const getExactRedemittelStructure = (examData) => {
    const userProvider = examData?.examProvider?.toLowerCase();
    const userLevel = examData?.level?.toLowerCase();
    const structure = {
        universal: {
            name: "Allgemein",
            icon: "🌐",
            description: "Universell verwendbare Redemittel",
            skills: {
                sprechen: {
                    name: "Sprechen",
                    icon: "🗣️",
                    parts: [
                        { id: "kontakt", name: "Kontakt aufnehmen" },
                        { id: "praesentation", name: "Präsentation" },
                        { id: "diskussion", name: "Diskussion" },
                        { id: "planung", name: "Gemeinsam planen" }
                    ]
                },
                schreiben: {
                    name: "Schreiben",
                    icon: "✍️",
                    parts: [
                        { id: "informell_email", name: "Informelle E-Mail" },
                        { id: "formell_email", name: "Formelle E-Mail" },
                        { id: "forumsbeitrag", name: "Forumsbeitrag" }
                    ]
                }
            }
        }
    };

    if (userProvider && userLevel) {
        const providerStructure = getExactProviderStructure(userProvider, userLevel);
        if (providerStructure) {
            structure[userProvider] = providerStructure;
        }
    }
    return structure;
};

// --- Main RedemittelPage Component ---
const RedemittelPage = ({ onBack, examData }) => {
    const navigate = useLocalizedNavigate();
    // Navigation and selection state
    const [currentView, setCurrentView] = useState('provider-list');
    const [viewingProvider, setViewingProvider] = useState(null);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [selectedPart, setSelectedPart] = useState(null);
    const [redemittelStructure, setRedemittelStructure] = useState({});

    // Session and card state
    const [isLoading, setIsLoading] = useState(true);
    const [sessionQueue, setSessionQueue] = useState([]);
    const [srsData, setSrsData] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    
    // Debug wrapper for setIsFlipped
    const handleFlipChange = useCallback((newState) => {
        console.log('📞 Parent setIsFlipped called with:', newState, {
            currentParentIsFlipped: isFlipped,
            currentIndex,
            cardId: sessionQueue[currentIndex]?.id
        });
        setIsFlipped(newState);
    }, [isFlipped, currentIndex, sessionQueue]);
    const [sessionComplete, setSessionComplete] = useState(false);
    const [sessionStats, setSessionStats] = useState({ again: 0, hard: 0, good: 0, easy: 0, studied: 0 });
    const [buttonLabels, setButtonLabels] = useState({});

    // Drag state for swipe gestures
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const startPos = useRef({ x: 0, y: 0 });
    const isTrackingDrag = useRef(false);

    // Hover state for back button
    const [isBackHovered, setIsBackHovered] = useState(false);

    // Subscription + aha modal
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [showAhaModal, setShowAhaModal] = useState(false);
    const [pendingExitNav, setPendingExitNav] = useState(null);

    const handleAhaDismiss = useCallback(() => {
        setShowAhaModal(false);
        if (pendingExitNav) {
            const fn = pendingExitNav;
            setPendingExitNav(null);
            fn();
        }
    }, [pendingExitNav]);

    const handleAhaUpgrade = useCallback(() => {
        setPendingExitNav(null);
        setShowAhaModal(false);
    }, []);

    // SRS Service instance
    const srsService = useRef(new AnkiSRS()).current;

    // Load structure on mount
    useEffect(() => {
        const structure = getExactRedemittelStructure(examData);
        setRedemittelStructure(structure);
        setIsLoading(false);
    }, [examData]);

    // Load subscription status
    useEffect(() => {
        const loadSubscription = async () => {
            if (auth.currentUser) {
                try {
                    const status = await subscriptionService.getSubscriptionStatus(auth.currentUser.uid);
                    setSubscriptionStatus(status);
                } catch {
                    setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
                }
            } else {
                setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
            }
        };
        loadSubscription();
    }, []);

    // Show aha modal after session complete for free users
    useEffect(() => {
        if (sessionComplete && subscriptionStatus?.isFree) {
            setShowAhaModal(true);
            trackPaywallShown({ context: 'redemittel' });
        }
    }, [sessionComplete, subscriptionStatus]);

    // Load cards and SRS data when a part is selected
    const handlePartSelect = async (partId) => {
        setSelectedPart(partId);
        setIsLoading(true);

        try {
            const user = auth.currentUser;
            if (!user) throw new Error("User not authenticated");

            // 1. Fetch static card data from JSON
            const level = examData?.level?.toLowerCase() || 'b1';
            const fileName = `${viewingProvider}-${level}-redemittel.json`;
            const response = await fetch(`/data/redemittel/${fileName}`);
            if (!response.ok) throw new Error(`Failed to load ${fileName}`);
            const allJsonData = await response.json();
            const skillData = allJsonData.skills?.[selectedSkill];
            const partData = skillData?.parts?.find(p => p.id === partId);
            const staticCards = partData?.cards || [];

            if (staticCards.length === 0) {
                console.warn("No cards found for this section.");
                setSessionQueue([]);
                setIsLoading(false);
                setCurrentView('practice');
                return;
            }

            // 2. Fetch or initialize SRS data from Firestore
            const progressDocRef = doc(db, `users/${user.uid}/redemittelProgress/${partId}`);
            const progressDoc = await getDoc(progressDocRef);
            const userSrsData = progressDoc.exists() ? progressDoc.data() : {};

            const combinedSrsData = {};
            staticCards.forEach(card => {
                combinedSrsData[card.id] = userSrsData[card.id] || srsService.initializeCard(card.id);
            });
            setSrsData(combinedSrsData);

            // 3. Create and sort the session queue
            const dueCards = srsService.getDueCards(Object.values(combinedSrsData));
            const cardMap = new Map(staticCards.map(c => [c.id, c]));
            const queue = srsService.sortByPriority(dueCards).map(srsRecord => ({
                ...cardMap.get(srsRecord.id),
                srs: srsRecord
            }));

            setSessionQueue(queue);
            setCurrentIndex(0);
            setSessionStats({ again: 0, hard: 0, good: 0, easy: 0, studied: 0 });
            setSessionComplete(false);
            setIsFlipped(false);
            if (queue.length > 0) {
                setButtonLabels(srsService.getButtonLabels(queue[0].srs));
            }

        } catch (error) {
            console.error("Error loading Redemittel session:", error);
            setSessionQueue([]);
        } finally {
            setIsLoading(false);
            setCurrentView('practice');
        }
    };

    // --- Swipe Handlers ---
    const handleDragStart = useCallback((clientX, clientY) => {
        if (isFlipped) return;
        // Mark that we're tracking a potential drag
        isTrackingDrag.current = true;
        startPos.current = { x: clientX, y: clientY };
        document.body.style.userSelect = 'none';
    }, [isFlipped]);

    const handleDragMove = useCallback((clientX, clientY) => {
        // Only process if we're actually tracking a drag (mousedown happened)
        if (!isTrackingDrag.current || isFlipped) return;
        const deltaX = clientX - startPos.current.x;
        const deltaY = clientY - startPos.current.y;
        const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        // Only start dragging if there's significant movement (more than 5px)
        if (movement > 5) {
            if (!isDragging) {
                setIsDragging(true);
            }
            setDragOffset({ x: deltaX, y: 0 });
        }
    }, [isDragging, isFlipped]);

    const handleDragEnd = useCallback(() => {
        // If we were dragging, handle swipe
        if (isDragging && !isFlipped) {
            const threshold = 100;
            if (Math.abs(dragOffset.x) > threshold) {
                const rating = dragOffset.x > 0 ? Rating.GOOD : Rating.AGAIN;
                handleRate(rating);
            } else {
                setDragOffset({ x: 0, y: 0 });
            }
        }
        // Reset drag state
        isTrackingDrag.current = false;
        setIsDragging(false);
        setDragOffset({ x: 0, y: 0 });
        startPos.current = { x: 0, y: 0 };
        document.body.style.userSelect = 'auto';
    }, [isDragging, isFlipped, dragOffset.x]);

    // --- Core SRS Logic ---
    const handleRate = async (rating) => {
        if (currentIndex >= sessionQueue.length) return;

        const currentCard = sessionQueue[currentIndex];
        const user = auth.currentUser;

        // 1. Calculate next review and update SRS data
        const updatedSrsRecord = srsService.calculateNextReview(currentCard.srs, rating);
        const newSrsData = { ...srsData, [currentCard.id]: updatedSrsRecord };
        setSrsData(newSrsData);

        // 2. Update stats
        const ratingKey = Object.keys(Rating).find(key => Rating[key] === rating).toLowerCase();
        setSessionStats(prev => ({
            ...prev,
            [ratingKey]: prev[ratingKey] + 1,
            studied: prev.studied + 1
        }));

        // 3. Save progress to Firestore
        if (user) {
            const progressDocRef = doc(db, `users/${user.uid}/redemittelProgress/${selectedPart}`);
            await setDoc(progressDocRef, newSrsData, { merge: true });
        }

        // 4. Move to the next card
        if (currentIndex < sessionQueue.length - 1) {
            setTimeout(() => {
                setIsFlipped(false);
                setDragOffset({ x: 0, y: 0 });
                setCurrentIndex(prev => prev + 1);
                setButtonLabels(srsService.getButtonLabels(sessionQueue[currentIndex + 1].srs));
            }, 250);
        } else {
            // Session complete
            setSessionComplete(true);
            logRedemittelLearning({
                provider: viewingProvider,
                skill: selectedSkill,
                part: selectedPart,
                cardsStudied: sessionStats.studied + 1,
                level: examData?.level || 'B1'
            }).catch(console.error);
        }
    };

    const handleFlip = () => {
        if (!isDragging) {
            setIsFlipped(!isFlipped);
        }
    };

    const restartSession = () => {
        handlePartSelect(selectedPart);
    };

    // --- Navigation and UI ---
    const handleBackNavigation = () => {
        switch (currentView) {
            case 'skill-list':
                setCurrentView('provider-list');
                setViewingProvider(null);
                break;
            case 'part-list':
                setCurrentView('skill-list');
                setSelectedSkill(null);
                break;
            case 'practice':
                if (subscriptionStatus?.isFree) {
                    setPendingExitNav(() => () => {
                        setCurrentView('part-list');
                        setSelectedPart(null);
                        setSessionQueue([]);
                    });
                    setShowAhaModal(true);
                    trackPaywallShown({ context: 'redemittel' });
                } else {
                    setCurrentView('part-list');
                    setSelectedPart(null);
                    setSessionQueue([]);
                }
                break;
            case 'stats':
                setCurrentView('part-list');
                setSelectedPart(null);
                setSessionQueue([]);
                break;
            default:
                onBack('/practice');
        }
    };

    const getPageTitle = () => {
        switch (currentView) {
            case 'provider-list': return 'Redemittel-Trainer';
            case 'skill-list': return redemittelStructure[viewingProvider]?.name || 'Fertigkeiten';
            case 'part-list': return redemittelStructure[viewingProvider]?.skills[selectedSkill]?.name || 'Teile';
            case 'practice':
                const part = redemittelStructure[viewingProvider]?.skills[selectedSkill]?.parts?.find(p => p.id === selectedPart);
                return part?.name || 'Üben';
            case 'stats': return 'Ergebnisse';
            default: return 'Redemittel';
        }
    };

    // --- Render Functions ---
    const renderProviderList = () => {
        const userProviderKey = examData?.examProvider?.toLowerCase();
        const userProvider = userProviderKey ? redemittelStructure[userProviderKey] : null;
        const universalProvider = redemittelStructure['universal'];

        return (
            <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 pb-20 sm:pb-8">
                {/* Header */}
                <div className="relative text-center mb-4 sm:mb-12">
                    <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white text-[10px] sm:text-sm font-bold tracking-widest uppercase rounded-full mb-3 sm:mb-6">
                        REDEMITTEL TRAINER
                    </div>
                    <h2 className="font-space text-xl sm:text-4xl lg:text-5xl font-black text-black mb-1.5 sm:mb-3 tracking-tight">
                        Wähle deinen Prüfungsanbieter
                    </h2>
                    <p className="text-sm sm:text-xl text-black/60 hidden sm:block">
                        Select your exam provider to practice
                    </p>
                </div>

                {/* Provider Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                    {userProvider && (
                        <div
                            onClick={() => { setViewingProvider(userProviderKey); setCurrentView('skill-list'); }}
                            className="redemittel-category-card p-4 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-white text-black cursor-pointer group touch-manipulation"
                        >
                            {/* Decorative Dots, hidden on mobile */}
                            <div className="redemittel-decorative-dot redemittel-dot-1 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-2 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-3 hidden sm:block"></div>

                            {/* Shine Effect */}
                            <div className="redemittel-shine-effect"></div>

                            {/* Corner Decorations, hidden on mobile */}
                            <div className="corner-decoration absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 hidden sm:block"></div>
                            <div className="corner-decoration absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 hidden sm:block"></div>

                            {/* Icon */}
                            <div className="redemittel-icon-animated w-12 h-12 sm:w-16 sm:h-16 bg-[#3A86FF] rounded-xl sm:rounded-2xl flex items-center justify-center text-2xl sm:text-3xl mb-2 sm:mb-4 mx-auto">
                                {userProvider.icon}
                            </div>

                            {/* Content */}
                            <div className="redemittel-content-animated text-center">
                                <h4 className="font-space font-black text-base sm:text-xl mb-1 sm:mb-2">{userProvider.name}</h4>
                                <p className="text-xs sm:text-sm text-black/60">
                                    {userProvider.description}
                                </p>
                            </div>
                        </div>
                    )}
                    {universalProvider && (
                        <div
                            onClick={() => { setViewingProvider('universal'); setCurrentView('skill-list'); }}
                            className="redemittel-category-card p-4 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-white text-black cursor-pointer group touch-manipulation"
                        >
                            {/* Decorative Dots, hidden on mobile */}
                            <div className="redemittel-decorative-dot redemittel-dot-1 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-2 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-3 hidden sm:block"></div>

                            {/* Shine Effect */}
                            <div className="redemittel-shine-effect"></div>

                            {/* Corner Decorations, hidden on mobile */}
                            <div className="corner-decoration absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 hidden sm:block"></div>
                            <div className="corner-decoration absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 hidden sm:block"></div>

                            {/* Icon */}
                            <div className="redemittel-icon-animated w-12 h-12 sm:w-16 sm:h-16 bg-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-2xl sm:text-3xl mb-2 sm:mb-4 mx-auto">
                                {universalProvider.icon}
                            </div>

                            {/* Content */}
                            <div className="redemittel-content-animated text-center">
                                <h4 className="font-space font-black text-base sm:text-xl mb-1 sm:mb-2">{universalProvider.name}</h4>
                                <p className="text-xs sm:text-sm text-black/60">
                                    {universalProvider.description}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom Navigation. Provider List */}
                <GrammarBottomNav buttons={[
                    { id: 'back', icon: BackIcon, label: 'Back', onClick: () => onBack('/practice'), color: '#3A86FF' },
                    { id: 'home', icon: Home, label: 'Home', onClick: () => navigate('/dashboard'), color: '#3A86FF' },
                    { id: 'practice', icon: BookOpen, label: 'Practice', onClick: () => navigate('/practice'), color: '#3A86FF' },
                ]} />
            </div>
        );
    };

    const renderSkillList = () => {
        const skills = redemittelStructure[viewingProvider]?.skills || {};
        return (
            <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 pb-20 sm:pb-8">
                {/* Header */}
                <div className="relative text-center mb-4 sm:mb-12">
                    <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white text-[10px] sm:text-sm font-bold tracking-widest uppercase rounded-full mb-3 sm:mb-6">
                        {redemittelStructure[viewingProvider]?.name || 'FERTIGKEITEN'}
                    </div>
                    <h2 className="font-space text-xl sm:text-4xl lg:text-5xl font-black text-black mb-1.5 sm:mb-3 tracking-tight">
                        Wähle eine Fertigkeit
                    </h2>
                    <p className="text-sm sm:text-xl text-black/60 hidden sm:block">
                        Choose a skill to practice
                    </p>
                </div>

                {/* Skills Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
                    {Object.entries(skills).map(([skillId, skill]) => (
                        <div
                            key={skillId}
                            onClick={() => { setSelectedSkill(skillId); setCurrentView('part-list'); }}
                            className="redemittel-category-card p-4 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-white text-black cursor-pointer group touch-manipulation"
                        >
                            {/* Decorative Dots, hidden on mobile */}
                            <div className="redemittel-decorative-dot redemittel-dot-1 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-2 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-3 hidden sm:block"></div>

                            {/* Shine Effect */}
                            <div className="redemittel-shine-effect"></div>

                            {/* Corner Decorations, hidden on mobile */}
                            <div className="corner-decoration absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 hidden sm:block"></div>
                            <div className="corner-decoration absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 hidden sm:block"></div>

                            {/* Icon */}
                            <div className="redemittel-icon-animated w-12 h-12 sm:w-16 sm:h-16 bg-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-2xl sm:text-3xl mb-2 sm:mb-4 mx-auto">
                                {skill.icon}
                            </div>

                            {/* Content */}
                            <div className="redemittel-content-animated text-center">
                                <h4 className="font-space font-black text-sm sm:text-xl mb-1 sm:mb-2 line-clamp-2">{skill.name}</h4>
                                <p className="text-xs sm:text-sm text-black/60">
                                    {skill.parts?.length || 0} Teile
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom Navigation. Skill List */}
                <GrammarBottomNav buttons={[
                    { id: 'back', icon: BackIcon, label: 'Back', onClick: () => { setCurrentView('provider-list'); setViewingProvider(null); }, color: '#3A86FF' },
                    { id: 'home', icon: Home, label: 'Home', onClick: () => navigate('/dashboard'), color: '#3A86FF' },
                    { id: 'practice', icon: BookOpen, label: 'Practice', onClick: () => navigate('/practice'), color: '#3A86FF' },
                ]} />
            </div>
        );
    };

    const renderPartList = () => {
        const parts = redemittelStructure[viewingProvider]?.skills[selectedSkill]?.parts || [];
        return (
            <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 pb-20 sm:pb-8">
                {/* Header */}
                <div className="relative text-center mb-4 sm:mb-12">
                    <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white text-[10px] sm:text-sm font-bold tracking-widest uppercase rounded-full mb-3 sm:mb-6">
                        {redemittelStructure[viewingProvider]?.skills[selectedSkill]?.name || 'TEILE'}
                    </div>
                    <h2 className="font-space text-xl sm:text-4xl lg:text-5xl font-black text-black mb-1.5 sm:mb-3 tracking-tight">
                        Wähle einen Teil
                    </h2>
                    <p className="text-sm sm:text-xl text-black/60 hidden sm:block">
                        Choose a specific part to practice
                    </p>
                </div>

                {/* Parts Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
                    {parts.map((part) => (
                        <div
                            key={part.id}
                            onClick={() => handlePartSelect(part.id)}
                            className="redemittel-category-card p-4 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-white text-black cursor-pointer group touch-manipulation"
                        >
                            {/* Decorative Dots, hidden on mobile */}
                            <div className="redemittel-decorative-dot redemittel-dot-1 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-2 hidden sm:block"></div>
                            <div className="redemittel-decorative-dot redemittel-dot-3 hidden sm:block"></div>

                            {/* Shine Effect */}
                            <div className="redemittel-shine-effect"></div>

                            {/* Corner Decorations, hidden on mobile */}
                            <div className="corner-decoration absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 hidden sm:block"></div>
                            <div className="corner-decoration absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 hidden sm:block"></div>

                            {/* Icon */}
                            <div className="redemittel-icon-animated w-12 h-12 sm:w-16 sm:h-16 bg-[#3A86FF] rounded-xl sm:rounded-2xl flex items-center justify-center text-2xl sm:text-3xl mb-2 sm:mb-4 mx-auto">
                                {part.icon || '📝'}
                            </div>

                            {/* Content */}
                            <div className="redemittel-content-animated text-center">
                                <h4 className="font-space font-black text-sm sm:text-xl mb-0 sm:mb-2 line-clamp-2">{part.name}</h4>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom Navigation. Part List */}
                <GrammarBottomNav buttons={[
                    { id: 'back', icon: BackIcon, label: 'Back', onClick: () => { setCurrentView('skill-list'); setSelectedSkill(null); }, color: '#3A86FF' },
                    { id: 'home', icon: Home, label: 'Home', onClick: () => navigate('/dashboard'), color: '#3A86FF' },
                    { id: 'practice', icon: BookOpen, label: 'Practice', onClick: () => navigate('/practice'), color: '#3A86FF' },
                ]} />
            </div>
        );
    };

    const renderPracticeView = () => {
        if (isLoading) return (
            <div className="flex justify-center items-center py-20">
                <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
            </div>
        );

        if (sessionComplete) return renderStatsView();

        if (sessionQueue.length === 0) return (
            <div className="text-center py-20 text-gray-600">
                Keine Karten für diese Lektion fällig. Gut gemacht!
            </div>
        );

        const currentCardData = sessionQueue[currentIndex];

        return (
            <div className="bg-white relative overflow-hidden p-1.5 sm:p-4 flex flex-col" style={{
                height: '100dvh',
                paddingTop: 'max(0.25rem, env(safe-area-inset-top))',
                paddingBottom: '0px',
            }}>
                {/* Background Pattern */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" />

                <div className="relative z-10 max-w-4xl mx-auto w-full flex-1 flex flex-col min-h-0">
                    {/* Header with Back Button and Stats, hidden on mobile */}
                    <div className="hidden sm:flex mb-8 justify-between items-center flex-shrink-0">
                        <button
                            onClick={handleBackNavigation}
                            onMouseEnter={() => setIsBackHovered(true)}
                            onMouseLeave={() => setIsBackHovered(false)}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
                        >
                            {/* Wave fill effect */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    right: 0,
                                    width: isBackHovered ? '100%' : '0%',
                                    background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
                                    transition: 'width 0.7s ease-out',
                                    zIndex: 0
                                }}
                            />
                            <ArrowLeft
                                className="w-5 h-5 relative z-10 transition-colors duration-500"
                                style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
                            />
                            <span
                                className="font-semibold relative z-10 transition-colors duration-500"
                                style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
                            >
                                Zurück
                            </span>
                        </button>

                        <div className="flex items-center gap-3 px-6 py-3 bg-white border-[3px] border-black/15 rounded-full shadow-lg">
                            <span className="text-sm font-medium text-black/60">Karten</span>
                            <span className="font-space font-black text-xl text-[#3A86FF]">{currentIndex + 1}</span>
                            <span className="text-black/40">/</span>
                            <span className="font-space font-bold text-lg text-black/60">{sessionQueue.length}</span>
                        </div>

                        <div className="flex items-center gap-3 px-6 py-3 bg-black text-white rounded-full shadow-lg">
                            <BarChart3 className="w-5 h-5" />
                            <span className="font-space font-bold text-lg">{sessionStats.studied}</span>
                        </div>
                    </div>

                    {/* Card Stack Area, constrained height to leave room for buttons + nav */}
                    <div
                        className="relative mb-2 sm:mb-6 flex-1 flex items-center justify-center min-h-0"
                        style={{ 
                            // On mobile: account for larger 2-row buttons (approx 160px) + nav (64px) + header
                            // On desktop: original calculation
                            maxHeight: 'calc(100dvh - min(450px, 350px))',
                            minHeight: '300px' // Minimum height for card visibility
                        }}
                        onMouseDown={(e) => {
                            // Only handle drag if not clicking on interactive elements
                            if (e.target.closest('button, a, [role="button"]')) return;
                            handleDragStart(e.clientX, e.clientY);
                        }}
                        onTouchStart={(e) => {
                            // Only handle drag if not touching interactive elements or card content
                            if (e.target.closest('button, a, [role="button"]')) return;
                            // Don't interfere with card's own touch handlers - let card handle taps
                            // Only start drag tracking, don't set isDragging yet
                            handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
                        }}
                        onMouseMove={(e) => {
                            // Only process if we're tracking a drag (mousedown happened)
                            if (isTrackingDrag.current) {
                                handleDragMove(e.clientX, e.clientY);
                            }
                        }}
                        onTouchMove={(e) => {
                            // Only process if we're tracking a drag (touchstart happened)
                            if (isTrackingDrag.current) {
                                handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
                            }
                        }}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                        onTouchEnd={handleDragEnd}
                    >
                        <div className="w-full max-w-2xl mx-auto h-full max-h-[600px] relative">
                            <SwipeIndicators
                                isDragging={isDragging}
                                dragOffset={dragOffset}
                                isFlipped={isFlipped}
                            />
                            {sessionQueue.slice(currentIndex, currentIndex + 3).reverse().map((card, index, array) => {
                                const isActiveCard = index === array.length - 1;
                                // Z-index: active card gets 10, others get their position (1, 2, 3...)
                                // Since array is reversed, index 0 is bottom (z: 1), index 1 is middle (z: 2), index 2 is top/active (z: 10)
                                const cardZIndex = isActiveCard ? 10 : (index + 1);
                                console.log('📚 Rendering card in stack:', {
                                    cardId: card.id,
                                    index,
                                    isActive: isActiveCard,
                                    calculatedZIndex: cardZIndex,
                                    totalCards: array.length
                                });
                                return (
                                    <RedemittelCard
                                        key={card.id}
                                        card={card}
                                        srsData={card.srs}
                                        onFlipChange={isActiveCard ? handleFlipChange : () => {}}
                                        dragOffset={isActiveCard ? dragOffset : {x: 0, y: 0}}
                                        isDragging={isActiveCard ? isDragging : false}
                                        isActive={isActiveCard}
                                        zIndex={cardZIndex}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Review Buttons, positioned right above bottom nav with proper spacing */}
                    <div 
                        className="flex-shrink-0 px-2 sm:px-0" 
                        style={{ 
                            // Ensure buttons are above navigation bar (nav is h-16 = 64px, plus safe area)
                            marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)',
                            paddingBottom: '0.5rem'
                        }}
                    >
                        <ReviewButtons
                            onRate={handleRate}
                            buttonLabels={buttonLabels}
                            isFlipped={isFlipped}
                        />
                    </div>
                </div>

                {/* Bottom Navigation. Practice View */}
                <GrammarBottomNav buttons={[
                    { id: 'back', icon: BackIcon, label: 'Back', onClick: handleBackNavigation, color: '#3A86FF' },
                    { id: 'progress', type: 'progress', label: 'Cards', progress: sessionQueue.length > 0 ? ((currentIndex + 1) / sessionQueue.length) * 100 : 0, current: currentIndex + 1, total: sessionQueue.length, color: '#3A86FF' },
                    { id: 'studied', icon: BarChart3, label: `${sessionStats.studied} Studied`, onClick: () => {}, active: true, color: '#3A86FF' },
                    { id: 'topics', icon: LayoutGrid, label: 'Topics', onClick: () => setCurrentView('provider-list'), color: '#3A86FF' },
                ]} />
            </div>
        );
    };

    const renderStatsView = () => {
        const partName = redemittelStructure[viewingProvider]?.skills[selectedSkill]?.parts?.find(p => p.id === selectedPart)?.name || '';

        return (
            <div className="max-w-2xl mx-auto px-4 py-8 pb-24 sm:pb-8 animate-fade-in">
                {sessionComplete && <Confetti recycle={false} numberOfPieces={400} />}
                <RedemittelStats
                    stats={sessionStats}
                    onContinue={restartSession}
                    examProvider={redemittelStructure[viewingProvider]?.name || ''}
                    level={examData?.level || 'B1'}
                    categoryName={partName}
                />

                {/* Bottom Navigation. Stats View */}
                <GrammarBottomNav buttons={[
                    { id: 'topics', icon: LayoutGrid, label: 'Topics', onClick: () => setCurrentView('part-list'), color: '#3A86FF' },
                    { id: 'home', icon: Home, label: 'Home', onClick: () => {
                        if (subscriptionStatus?.isFree) {
                            setPendingExitNav(() => () => navigate('/dashboard'));
                            setShowAhaModal(true);
                            trackPaywallShown({ context: 'redemittel' });
                        } else {
                            navigate('/dashboard');
                        }
                    }, color: '#3A86FF' },
                    { id: 'practice', icon: BookOpen, label: 'Practice', onClick: () => {
                        if (subscriptionStatus?.isFree) {
                            setPendingExitNav(() => () => navigate('/practice'));
                            setShowAhaModal(true);
                            trackPaywallShown({ context: 'redemittel' });
                        } else {
                            navigate('/practice');
                        }
                    }, color: '#3A86FF' },
                ]} />
            </div>
        );
    };

    const renderContent = () => {
        switch (currentView) {
            case 'provider-list': return renderProviderList();
            case 'skill-list': return renderSkillList();
            case 'part-list': return renderPartList();
            case 'practice': return renderPracticeView();
            case 'stats': return renderStatsView();
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto py-2 sm:py-4 px-2 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-4">
                            <button
                                onClick={handleBackNavigation}
                                className="hidden sm:flex p-2 rounded-lg border-2 border-black hover:bg-black hover:text-white transition-all touch-manipulation"
                            >
                                <BackIcon className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-sm sm:text-xl font-bold text-gray-900">{getPageTitle()}</h1>
                                <p className="text-[10px] sm:text-sm text-gray-600">Niveau {examData?.level || 'B1'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="pb-8 relative z-10">{renderContent()}</main>

            {/* Inject Card Styles */}
            <style dangerouslySetInnerHTML={{ __html: cardStyles }} />

            <AhaUpsellModal
                open={showAhaModal}
                onDismiss={handleAhaDismiss}
                onUpgrade={handleAhaUpgrade}
                context="redemittel"
            />
        </div>
    );
};

export default RedemittelPage;